import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Button, Spinner, Nav } from 'react-bootstrap';
import { 
  FaTrophy, 
  FaChartLine, 
  FaBriefcase, 
  FaMoneyBillWave, 
  FaMedal, 
  FaStar, 
  FaPlus, 
  FaEdit, 
  FaTrash, 
  FaFlag, 
  FaCalendarAlt, 
  FaUserCheck, 
  FaAward,
  FaArrowRight,
  FaLayerGroup,
  FaCheckCircle
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import { useAuth } from '../../context/AuthContext';
import { getEmployeeCareerJourney, deleteCareerMilestone } from '../../services/firebase/employeeService';
import AddCareerMilestoneModal from './AddCareerMilestoneModal';
import EditCareerMilestoneModal from './EditCareerMilestoneModal';

const CareerGrowthSection = ({ employee, onRefresh }) => {
  const { user } = useAuth();
  const [journey, setJourney] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState('all');
  
  // Modals state
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedMilestone, setSelectedMilestone] = useState(null);

  // Permission checks
  const isHROrAdmin = user?.role === 'hr' || user?.role === 'admin' || user?.role === 'superadmin';

  useEffect(() => {
    if (employee?.id) {
      fetchJourney();
    }
  }, [employee]);

  const fetchJourney = async () => {
    setLoading(true);
    try {
      const res = await getEmployeeCareerJourney(employee.id);
      if (res.success) {
        setJourney(res.data || []);
      }
    } catch (err) {
      console.error('Error loading career journey:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (milestoneId) => {
    if (!window.confirm('Are you sure you want to delete this career milestone?')) return;
    try {
      await deleteCareerMilestone(employee.id, milestoneId, user);
      toast.success('Milestone deleted successfully');
      fetchJourney();
      if (onRefresh) onRefresh();
    } catch (err) {
      console.error('Error deleting milestone:', err);
      toast.error('Failed to delete milestone');
    }
  };

  // ── Growth Summary Metrics Calculations ─────────────────────────────────
  const calculateMetrics = () => {
    const currentSalary = parseFloat(employee.salary?.basicSalary) || 0;
    
    let initialSalary = currentSalary;
    const salaryEvents = [...journey]
      .filter(m => (m.previousSalary && m.previousSalary > 0) || (m.newSalary && m.newSalary > 0))
      .sort((a, b) => new Date(a.effectiveDate || 0) - new Date(b.effectiveDate || 0));

    if (salaryEvents.length > 0) {
      initialSalary = salaryEvents[0].previousSalary || salaryEvents[0].newSalary || currentSalary;
    }

    const totalSalaryGrowthPct = initialSalary > 0 
      ? (((currentSalary - initialSalary) / initialSalary) * 100).toFixed(1) 
      : 0;

    const promotionsCount = journey.filter(m => 
      ['promotion', 'promotion_and_increment', 'role_change'].includes(m.type) && m.id !== 'baseline_joining'
    ).length;

    const awardsCount = journey.filter(m => 
      ['achievement', 'award'].includes(m.type) || !!m.badgeName
    ).length;

    const joinDateRaw = employee.job?.joiningDate || employee.joiningDate;
    let tenureText = 'New Joiner';
    let joinedDateFormatted = 'N/A';
    if (joinDateRaw) {
      const joinDate = joinDateRaw?.toDate ? joinDateRaw.toDate() : new Date(joinDateRaw);
      const now = new Date();
      if (!isNaN(joinDate.getTime())) {
        joinedDateFormatted = joinDate.toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' });
        const diffMonths = (now.getFullYear() - joinDate.getFullYear()) * 12 + (now.getMonth() - joinDate.getMonth());
        const years = Math.floor(diffMonths / 12);
        const months = diffMonths % 12;
        if (years > 0) {
          tenureText = `${years} yr${years > 1 ? 's' : ''} ${months > 0 ? `${months} mo` : ''}`;
        } else {
          tenureText = `${Math.max(1, months)} month${months > 1 ? 's' : ''}`;
        }
      }
    }

    return {
      initialSalary,
      currentSalary,
      totalSalaryGrowthPct,
      promotionsCount,
      awardsCount,
      tenureText,
      joinedDateFormatted
    };
  };

  const metrics = calculateMetrics();

  // ── Milestone Visual Themes ───────────────────────────────────────────
  const getMilestoneTheme = (type) => {
    switch (type) {
      case 'promotion_and_increment':
        return { 
          color: '#6d28d9', 
          bg: '#f5f3ff', 
          border: '#7c3aed', 
          iconBg: '#ede9fe',
          iconColor: '#6d28d9',
          icon: <FaChartLine />, 
          label: 'Promotion + Hike' 
        };
      case 'promotion':
        return { 
          color: '#1d4ed8', 
          bg: '#eff6ff', 
          border: '#2563eb', 
          iconBg: '#dbeafe',
          iconColor: '#1d4ed8',
          icon: <FaBriefcase />, 
          label: 'Promotion' 
        };
      case 'salary_increment':
        return { 
          color: '#047857', 
          bg: '#ecfdf5', 
          border: '#059669', 
          iconBg: '#d1fae5',
          iconColor: '#047857',
          icon: <FaMoneyBillWave />, 
          label: 'Salary Increment' 
        };
      case 'role_change':
        return { 
          color: '#0369a1', 
          bg: '#f0f9ff', 
          border: '#0284c7', 
          iconBg: '#e0f2fe',
          iconColor: '#0369a1',
          icon: <FaBriefcase />, 
          label: 'Role Shift' 
        };
      case 'achievement':
        return { 
          color: '#b45309', 
          bg: '#fffbeb', 
          border: '#d97706', 
          iconBg: '#fef3c7',
          iconColor: '#b45309',
          icon: <FaTrophy />, 
          label: 'Achievement' 
        };
      case 'award':
        return { 
          color: '#be123c', 
          bg: '#fff1f2', 
          border: '#e11d48', 
          iconBg: '#ffe4e6',
          iconColor: '#be123c',
          icon: <FaMedal />, 
          label: 'Award' 
        };
      case 'appraisal':
        return { 
          color: '#4338ca', 
          bg: '#eef2ff', 
          border: '#4f46e5', 
          iconBg: '#e0e7ff',
          iconColor: '#4338ca',
          icon: <FaStar />, 
          label: 'Appraisal' 
        };
      case 'joining':
      default:
        return { 
          color: '#0e7490', 
          bg: '#ecfeff', 
          border: '#0891b2', 
          iconBg: '#cffafe',
          iconColor: '#0e7490',
          icon: <FaFlag />, 
          label: 'Journey Started' 
        };
    }
  };

  // ── Filter Logic ───────────────────────────────────────────────────────
  const filteredJourney = journey.filter(m => {
    if (filterType === 'all') return true;
    if (filterType === 'promotions') return ['promotion', 'promotion_and_increment', 'role_change'].includes(m.type);
    if (filterType === 'salary') return ['salary_increment', 'promotion_and_increment', 'appraisal'].includes(m.type) || (m.newSalary && m.newSalary > 0);
    if (filterType === 'achievements') return ['achievement', 'award'].includes(m.type) || !!m.badgeName;
    return true;
  });

  return (
    <div className="career-growth-section">
      {/* ── 1. Top Summary Metric Cards (Clean Enterprise Style) ────────────── */}
      {/* ── 1. Top Summary Metric Cards (Clean Enterprise Style) ────────────── */}
      <Row className="g-2 g-md-3 mb-3 mb-md-4">
        {/* Salary Growth Metric */}
        <Col xs={6} md={3}>
          <Card className="border-0 shadow-sm h-100 rounded-3" style={{ borderTop: '4px solid #10b981', background: '#ffffff' }}>
            <Card.Body className="p-2 p-md-3">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <span className="text-muted fw-bold" style={{ fontSize: '0.68rem', letterSpacing: '0.5px' }}>SALARY GROWTH</span>
                <div className="rounded-circle p-1 p-md-2 d-flex align-items-center justify-content-center" style={{ background: '#d1fae5', color: '#059669', width: '30px', height: '30px' }}>
                  <FaMoneyBillWave size={14} />
                </div>
              </div>
              <div className="d-flex align-items-baseline gap-1">
                <h4 className="fw-bold mb-0 text-dark fs-5 fs-md-4">₹{metrics.currentSalary.toLocaleString('en-IN')}</h4>
                <small className="text-muted" style={{ fontSize: '0.7rem' }}>/ month</small>
              </div>
              <div className="mt-2 mt-md-3 pt-2 border-top d-flex justify-content-between align-items-center">
                <span className="text-muted" style={{ fontSize: '0.7rem' }}>Base: ₹{metrics.initialSalary.toLocaleString('en-IN')}</span>
                {metrics.totalSalaryGrowthPct > 0 ? (
                  <span style={{ background: '#d1fae5', color: '#047857', border: '1px solid #a7f3d0', fontSize: '0.7rem', fontWeight: 700, padding: '2px 6px', borderRadius: '6px' }}>
                    +{metrics.totalSalaryGrowthPct}% Hike
                  </span>
                ) : (
                  <span style={{ background: '#f1f5f9', color: '#475569', border: '1px solid #e2e8f0', fontSize: '0.7rem', fontWeight: 600, padding: '2px 6px', borderRadius: '6px' }}>
                    Initial Level
                  </span>
                )}
              </div>
            </Card.Body>
          </Card>
        </Col>

        {/* Promotions Metric */}
        <Col xs={6} md={3}>
          <Card className="border-0 shadow-sm h-100 rounded-3" style={{ borderTop: '4px solid #3b82f6', background: '#ffffff' }}>
            <Card.Body className="p-2 p-md-3">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <span className="text-muted fw-bold" style={{ fontSize: '0.68rem', letterSpacing: '0.5px' }}>LEVEL-UPS & ROLES</span>
                <div className="rounded-circle p-1 p-md-2 d-flex align-items-center justify-content-center" style={{ background: '#dbeafe', color: '#2563eb', width: '30px', height: '30px' }}>
                  <FaBriefcase size={14} />
                </div>
              </div>
              <h4 className="fw-bold mb-0 text-dark fs-5 fs-md-4">{metrics.promotionsCount}</h4>
              <div className="mt-2 mt-md-3 pt-2 border-top">
                <span className="text-muted d-block text-truncate" style={{ fontSize: '0.7rem' }}>
                  Role: <strong className="text-dark">{employee.job?.designation || employee.designation || 'Specialist'}</strong>
                </span>
              </div>
            </Card.Body>
          </Card>
        </Col>

        {/* Achievements Metric */}
        <Col xs={6} md={3}>
          <Card className="border-0 shadow-sm h-100 rounded-3" style={{ borderTop: '4px solid #f59e0b', background: '#ffffff' }}>
            <Card.Body className="p-2 p-md-3">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <span className="text-muted fw-bold" style={{ fontSize: '0.68rem', letterSpacing: '0.5px' }}>RECOGNITIONS</span>
                <div className="rounded-circle p-1 p-md-2 d-flex align-items-center justify-content-center" style={{ background: '#fef3c7', color: '#d97706', width: '30px', height: '30px' }}>
                  <FaTrophy size={14} />
                </div>
              </div>
              <h4 className="fw-bold mb-0 text-dark fs-5 fs-md-4">{metrics.awardsCount}</h4>
              <div className="mt-2 mt-md-3 pt-2 border-top">
                <span className="text-muted d-block text-truncate" style={{ fontSize: '0.7rem' }}>
                  Badges & Honors
                </span>
              </div>
            </Card.Body>
          </Card>
        </Col>

        {/* Tenure Metric */}
        <Col xs={6} md={3}>
          <Card className="border-0 shadow-sm h-100 rounded-3" style={{ borderTop: '4px solid #8b5cf6', background: '#ffffff' }}>
            <Card.Body className="p-2 p-md-3">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <span className="text-muted fw-bold" style={{ fontSize: '0.68rem', letterSpacing: '0.5px' }}>TENURE</span>
                <div className="rounded-circle p-1 p-md-2 d-flex align-items-center justify-content-center" style={{ background: '#ede9fe', color: '#7c3aed', width: '30px', height: '30px' }}>
                  <FaFlag size={14} />
                </div>
              </div>
              <h4 className="fw-bold mb-0 text-dark fs-5 fs-md-4">{metrics.tenureText}</h4>
              <div className="mt-2 mt-md-3 pt-2 border-top">
                <span className="text-muted d-block text-truncate" style={{ fontSize: '0.7rem' }}>
                  Joined: <strong className="text-dark">{metrics.joinedDateFormatted}</strong>
                </span>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* ── 2. Filter Navigation & Action Header ───────────────────────────── */}
      <Card className="border-0 shadow-sm mb-3 mb-md-4 bg-white rounded-3">
        <Card.Body className="p-2 p-md-3 d-flex flex-wrap justify-content-between align-items-center gap-2">
          {/* Filter Pills with horizontal scroll on touch */}
          <Nav variant="pills" activeKey={filterType} onSelect={(k) => setFilterType(k)} className="gap-1 gap-md-2 flex-nowrap overflow-auto py-1 w-100 w-md-auto" style={{ scrollbarWidth: 'none' }}>
            <Nav.Item>
              <Nav.Link 
                eventKey="all" 
                className={`rounded-3 px-2 px-md-3 py-1 py-md-2 small fw-bold d-flex align-items-center gap-1 text-nowrap ${filterType === 'all' ? 'bg-primary text-white' : 'bg-light text-dark'}`}
              >
                <FaLayerGroup /> All ({journey.length})
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link 
                eventKey="promotions" 
                className={`rounded-3 px-2 px-md-3 py-1 py-md-2 small fw-bold d-flex align-items-center gap-1 text-nowrap ${filterType === 'promotions' ? 'bg-primary text-white' : 'bg-light text-dark'}`}
              >
                <FaBriefcase /> Roles
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link 
                eventKey="salary" 
                className={`rounded-3 px-2 px-md-3 py-1 py-md-2 small fw-bold d-flex align-items-center gap-1 text-nowrap ${filterType === 'salary' ? 'bg-primary text-white' : 'bg-light text-dark'}`}
              >
                <FaMoneyBillWave /> Salary
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link 
                eventKey="achievements" 
                className={`rounded-3 px-2 px-md-3 py-1 py-md-2 small fw-bold d-flex align-items-center gap-1 text-nowrap ${filterType === 'achievements' ? 'bg-primary text-white' : 'bg-light text-dark'}`}
              >
                <FaTrophy /> Awards
              </Nav.Link>
            </Nav.Item>
          </Nav>

          {/* Add Milestone Action Button (HR & Admin only) */}
          {isHROrAdmin && (
            <Button 
              variant="primary" 
              className="fw-bold d-flex align-items-center gap-2 px-3 py-2 rounded-3 shadow-sm"
              onClick={() => setShowAddModal(true)}
            >
              <FaPlus /> Record Career Milestone / Increment
            </Button>
          )}
        </Card.Body>
      </Card>

      {/* ── 3. Interactive Career Growth Timeline ──────────────────────────── */}
      {loading ? (
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
          <p className="mt-2 text-muted small">Loading career growth timeline...</p>
        </div>
      ) : filteredJourney.length === 0 ? (
        <Card className="border-0 shadow-sm p-5 text-center bg-white rounded-3">
          <FaBriefcase className="text-muted fs-1 mx-auto mb-3 opacity-50" />
          <h5 className="fw-bold text-dark">No Milestones Found</h5>
          <p className="text-muted small">No career growth events matching the selected filter.</p>
          {isHROrAdmin && (
            <div className="mt-2">
              <Button variant="outline-primary" size="sm" onClick={() => setShowAddModal(true)}>
                <FaPlus className="me-1" /> Add First Milestone
              </Button>
            </div>
          )}
        </Card>
      ) : (
        <div className="timeline-container ps-1 pe-1">
          {filteredJourney.map((milestone, idx) => {
            const theme = getMilestoneTheme(milestone.type);
            const isFirst = idx === 0;
            const isJoining = milestone.type === 'joining' || milestone.id === 'baseline_joining';

            return (
              <div 
                key={milestone.id || idx} 
                className="milestone-card mb-4 position-relative bg-white"
                style={{
                  border: '1px solid #e2e8f0',
                  borderLeft: `5px solid ${theme.border}`,
                  borderRadius: '10px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                }}
              >
                <div className="p-4">
                  {/* Header Row */}
                  <div className="d-flex flex-wrap justify-content-between align-items-start gap-2 mb-3">
                    <div className="d-flex align-items-center gap-3">
                      <div 
                        className="rounded-circle d-flex align-items-center justify-content-center shadow-sm"
                        style={{
                          width: '42px',
                          height: '42px',
                          background: theme.iconBg,
                          color: theme.iconColor,
                          fontSize: '1.2rem',
                          flexShrink: 0
                        }}
                      >
                        {theme.icon}
                      </div>
                      <div>
                        <div className="d-flex align-items-center gap-2 flex-wrap">
                          <h5 className="fw-bold text-dark mb-0" style={{ fontSize: '1.05rem' }}>{milestone.title}</h5>
                          
                          {/* Polished, Clear High-Contrast Type Badge */}
                          <span 
                            style={{ 
                              background: theme.bg, 
                              color: theme.color, 
                              border: `1px solid ${theme.border}`,
                              fontSize: '0.72rem',
                              fontWeight: 700,
                              letterSpacing: '0.5px',
                              padding: '3px 10px',
                              borderRadius: '6px',
                              textTransform: 'uppercase',
                              display: 'inline-block'
                            }}
                          >
                            {theme.label}
                          </span>

                          {isFirst && !isJoining && (
                            <span 
                              style={{
                                background: '#dcfce7',
                                color: '#15803d',
                                border: '1px solid #86efac',
                                fontSize: '0.72rem',
                                fontWeight: 700,
                                padding: '3px 10px',
                                borderRadius: '6px',
                                textTransform: 'uppercase'
                              }}
                            >
                              Latest
                            </span>
                          )}
                        </div>
                        <div className="text-muted small d-flex align-items-center gap-1 mt-1">
                          <FaCalendarAlt className="text-secondary" /> Effective Date: <strong className="text-dark">{milestone.effectiveDate || 'Immediate'}</strong>
                        </div>
                      </div>
                    </div>

                    {/* Actions (HR/Admin only) */}
                    {isHROrAdmin && !isJoining && (
                      <div className="d-flex gap-2">
                        <Button 
                          variant="light" 
                          size="sm" 
                          className="border text-primary px-2"
                          onClick={() => {
                            setSelectedMilestone(milestone);
                            setShowEditModal(true);
                          }}
                          title="Edit Milestone"
                        >
                          <FaEdit />
                        </Button>
                        <Button 
                          variant="light" 
                          size="sm" 
                          className="border text-danger px-2"
                          onClick={() => handleDelete(milestone.id)}
                          title="Delete Milestone"
                        >
                          <FaTrash />
                        </Button>
                      </div>
                    )}
                  </div>

                  {/* ── Visual Comparison Blocks ─────────────────────────────── */}
                  <div className="p-3 rounded-3 mb-3" style={{ background: '#f8fafc', border: '1px solid #e2e8f0' }}>
                    <Row className="g-3">
                      {/* Promotion / Designation Diff */}
                      {milestone.newDesignation && (
                        <Col md={6}>
                          <span className="text-muted fw-bold d-block mb-1" style={{ fontSize: '0.72rem', letterSpacing: '0.5px' }}>
                            DESIGNATION / POSITION
                          </span>
                          <div className="d-flex align-items-center gap-2 flex-wrap">
                            {milestone.previousDesignation && milestone.previousDesignation !== milestone.newDesignation && (
                              <span className="text-muted text-decoration-line-through small">
                                {milestone.previousDesignation}
                              </span>
                            )}
                            {milestone.previousDesignation && milestone.previousDesignation !== milestone.newDesignation && (
                              <FaArrowRight className="text-primary small" />
                            )}
                            <span className="fw-bold text-dark px-3 py-1 rounded bg-white border shadow-sm" style={{ fontSize: '0.92rem' }}>
                              {milestone.newDesignation}
                            </span>
                          </div>
                        </Col>
                      )}

                      {/* Department Diff */}
                      {milestone.newDepartment && (
                        <Col md={6}>
                          <span className="text-muted fw-bold d-block mb-1" style={{ fontSize: '0.72rem', letterSpacing: '0.5px' }}>
                            DEPARTMENT
                          </span>
                          <div className="d-flex align-items-center gap-2 flex-wrap">
                            {milestone.previousDepartment && milestone.previousDepartment !== milestone.newDepartment && (
                              <span className="text-muted text-decoration-line-through small">
                                {milestone.previousDepartment}
                              </span>
                            )}
                            {milestone.previousDepartment && milestone.previousDepartment !== milestone.newDepartment && (
                              <FaArrowRight className="text-primary small" />
                            )}
                            <span className="fw-bold text-dark px-3 py-1 rounded bg-white border shadow-sm" style={{ fontSize: '0.92rem' }}>
                              {milestone.newDepartment}
                            </span>
                          </div>
                        </Col>
                      )}

                      {/* Salary Revision Diff */}
                      {milestone.newSalary > 0 && (
                        <Col md={6}>
                          <span className="text-muted fw-bold d-block mb-1" style={{ fontSize: '0.72rem', letterSpacing: '0.5px' }}>
                            BASE SALARY PROGRESSION
                          </span>
                          <div className="d-flex align-items-center gap-2 flex-wrap">
                            {milestone.previousSalary > 0 && milestone.previousSalary !== milestone.newSalary && (
                              <span className="text-muted text-decoration-line-through small">
                                ₹{milestone.previousSalary.toLocaleString('en-IN')}
                              </span>
                            )}
                            {milestone.previousSalary > 0 && milestone.previousSalary !== milestone.newSalary && (
                              <FaArrowRight className="text-success small" />
                            )}
                            <span className="fw-bold text-success px-3 py-1 rounded bg-white border border-success shadow-sm" style={{ fontSize: '0.92rem' }}>
                              ₹{milestone.newSalary.toLocaleString('en-IN')} / month
                            </span>
                            {milestone.incrementPercentage > 0 && (
                              <span style={{ background: '#d1fae5', color: '#047857', border: '1px solid #a7f3d0', fontSize: '0.75rem', fontWeight: 700, padding: '3px 8px', borderRadius: '6px' }}>
                                +{milestone.incrementPercentage}% Hike
                              </span>
                            )}
                          </div>
                        </Col>
                      )}

                      {/* Achievement / Badge Pill */}
                      {milestone.badgeName && (
                        <Col md={6}>
                          <span className="text-muted fw-bold d-block mb-1" style={{ fontSize: '0.72rem', letterSpacing: '0.5px' }}>
                            AWARDED BADGE / TITLE
                          </span>
                          <div className="d-inline-flex align-items-center gap-2 px-3 py-1 rounded-pill" style={{ background: '#fef3c7', color: '#92400e', border: '1px solid #fde68a', fontWeight: 700, fontSize: '0.85rem' }}>
                            <FaAward className="text-warning" />
                            <span>{milestone.badgeName}</span>
                          </div>
                        </Col>
                      )}
                    </Row>
                  </div>

                  {/* Description / Appraisal Notes */}
                  {milestone.description && (
                    <div className="mb-2 text-dark small p-3 rounded-3" style={{ background: '#f1f5f9', borderLeft: '4px solid #3b82f6', fontSize: '0.88rem' }}>
                      <strong className="text-primary">Remarks / Justification:</strong> {milestone.description}
                    </div>
                  )}

                  {/* Audit Footer */}
                  <div className="d-flex justify-content-between align-items-center pt-2 border-top text-muted small mt-3">
                    <span className="d-flex align-items-center gap-1">
                      <FaUserCheck className="text-success" /> Recorded by: <strong className="text-dark">{milestone.createdByName || 'System'}</strong>
                    </span>
                    {milestone.createdAt && (
                      <span className="text-muted">
                        Logged on: {new Date(milestone.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── 4. Add Milestone Modal ─────────────────────────────────────────── */}
      {showAddModal && (
        <AddCareerMilestoneModal
          show={showAddModal}
          onHide={() => setShowAddModal(false)}
          employee={employee}
          currentUser={user}
          onSuccess={() => {
            fetchJourney();
            if (onRefresh) onRefresh();
          }}
        />
      )}

      {/* ── 5. Edit Milestone Modal ────────────────────────────────────────── */}
      {showEditModal && selectedMilestone && (
        <EditCareerMilestoneModal
          show={showEditModal}
          onHide={() => {
            setShowEditModal(false);
            setSelectedMilestone(null);
          }}
          employee={employee}
          milestone={selectedMilestone}
          currentUser={user}
          onSuccess={() => {
            fetchJourney();
            if (onRefresh) onRefresh();
          }}
        />
      )}
    </div>
  );
};

export default CareerGrowthSection;
